import memberstackDOM from '@memberstack/dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { httpBatchLink, httpLink, splitLink, unstable_httpSubscriptionLink } from '@trpc/client'
import { createTRPCReact } from '@trpc/react-query'
import 'hover.css'
import { useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import type { AppRouter } from '../../../src/server'
import './App.css'
import './lib/amplitude'

export const memberstack = memberstackDOM.init({
  publicKey: 'pk_971c0de956b797603818',
})

import { TooltipProvider } from '@/components/shadcn/tooltip.tsx'
import { usePostHog } from 'posthog-js/react'
import { useLocalStorage } from 'usehooks-ts'
import { BrainBreakProvider } from './components/BrainBreakComponent'
import { WorkOSUser } from './components/UserProfileSettingDetail'
// Import the generated route tree
import { routeTree } from './routeTree.gen'

export const router = createRouter({
  routeTree,
  context: {
    auth: undefined!,
  },
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

declare module '@trpc/client' {
  interface OperationContext {
    batch?: boolean
    idempotent?: boolean
  }
}

export const trpc = createTRPCReact<AppRouter>()

const publicPages: Array<string> = [
  '/share/templates',
  '/share/inspiration',
  '/reactivation',
  '/reactivation?plan=spark-plan-no-trial-usd-Monthly',
  '/reactivation?plan=creativeos-standard-no-trial',
  '/reactivation?plan=creativeos-pro-no-trial',
  '/funnel-analyzer'
]
const UNAUTHORIZED_STATUS = 401
const SERVER_ERROR_STATUS = 500

const LOGIN_PATH = '/login?redirect=true'

const clearAuthData = () => {
  localStorage.removeItem('_wos_user')
  document.body.classList.remove('dark')
}

export const cFetch = async (url: RequestInfo | URL, options?: RequestInit) => {
  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
    })

    const currentPath = window.location.pathname
    const isPublicPage = publicPages.some((page) => currentPath.startsWith(page))

    if (response.status === UNAUTHORIZED_STATUS) {
      if (!isPublicPage) {
        clearAuthData()
        window.location.href = LOGIN_PATH
      }
    }
    if (response.status === SERVER_ERROR_STATUS) {
      const error = await response.json()
      if (error?.error?.message?.includes('invalid_grant') && !isPublicPage) {
        clearAuthData()
        window.location.href = LOGIN_PATH
      }
    }

    return response
  } catch (error) {
    console.error('Fetch error:', error)
    throw error
  }
}

// Function to show/hide the reCAPTCHA badge
export const toggleCaptchaBadge = (show: boolean) => {
  const badge = document.getElementsByClassName('grecaptcha-badge')[0]
  if (badge && badge instanceof HTMLElement) {
    badge.style.visibility = show ? 'visible' : 'hidden'
  }
}

export function App() {
  const [queryClient] = useState(() => new QueryClient())
  const [trpcClient] = useState(() =>
    trpc.createClient({
      links: [
        splitLink({
          condition(op) {
            return op.type === 'subscription' // Direct subscriptions to SSE
          },
          true: unstable_httpSubscriptionLink({
            url: import.meta.env.VITE_API_URL as string, // SSE URL
            eventSourceOptions() {
              return {
                withCredentials: true, // <---
              }
            },
          }),
          false: splitLink({
            condition(op) {
              // Batch only specific procedures
              return op.context.batch || false
            },
            true: httpBatchLink({
              url: import.meta.env.VITE_API_URL as string,
              fetch: cFetch,
              headers(opts) {
                return {
                  'X-Request-Id': uuidv4(),
                  ...(opts.opList[0]?.context.idempotent ? { idempotent: 'true' } : {}),
                }
              },
            }),
            false: httpLink({
              url: import.meta.env.VITE_API_URL as string,
              fetch: cFetch,
              headers(opts) {
                return {
                  'X-Request-Id': uuidv4(),
                  ...(opts.op.context.idempotent ? { idempotent: 'true' } : {}),
                }
              },
            }),
          }),
        }),
      ],
    }),
  )
  const posthog = usePostHog()

  const [user] = useLocalStorage<WorkOSUser | undefined>('_wos_user', undefined)

  useEffect(() => {
    window.Chargebee.init({
      site: import.meta.env.VITE_CHARGEBEE_SITE,
      publishableKey: import.meta.env.VITE_CHARGEBEE_PUBLISHABLE_KEY,
    })
  }, [])

  useEffect(() => {
    if (user && user.email && user.id) {
      // Identify sends an event, so you want may want to limit how often you call it
      posthog?.identify(user.id, {
        email: user.email,
      })
    }
  }, [posthog, user])

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <BrainBreakProvider>
          <TooltipProvider>
            <RouterProvider
              router={router}
              context={{
                auth: {
                  isAuthenticated: user ? true : false,
                  isAuthenticatedNextRoute: '/feeds/templates',
                },
              }}
            />
          </TooltipProvider>
        </BrainBreakProvider>
      </QueryClientProvider>
    </trpc.Provider>
  )
}
